import React, { useState } from "react";
import api from "../../api";
import { Loader } from "./loader";
import moment from "moment";
import {
  Search,
  CreditCard,
  Database,
  User,
  FileText,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Mail,
  Phone,
  MapPin,
  Cake,
  ExternalLink,
  Copy,
  RefreshCw,
} from "lucide-react";
import "./CheckStripeCustomerData.css";
import { useAlert } from "react-alert";

function CheckSquareCustomerData() {
  const alert = useAlert();
  const [loader, setLoader] = useState(false);
  const [searchMail, setSearchMail] = useState("");
  const [customerData, setCustomerData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dbUser, setDbUser] = useState([]);
  const [dbLogs, setDbLogs] = useState([]);

  const handleSearch = async () => {
    try {
      if (!searchMail) {
        alert.error("Please insert email for search");
        return;
      }
      setLoader(true);
      const res = await api.getSquareCustomerData({ email: searchMail });
      if (res?.success) {
        setCustomerData(res?.customer || null);
        setSubscriptions(res?.subscriptions || []);
        setPayments(res?.payments || []);
        setDbUser(res?.dbUser || []);
        setDbLogs(res?.dbLogs || []);
        if (!res?.customer && !res?.dbUser?.length) {
          alert.error("No data found for this email");
        }
      } else {
        alert.error("No data found for this email");
      }
      setLoader(false);
    } catch (error) {
      alert.error(error?.response?.data?.message || "Invalid Email or not found in Square");
      setLoader(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert.info("Copied to clipboard!");
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("MMM Do YYYY, h:mm a");
  };

  return (
    <>
      <div className="stripe-data-container">
        <div className="stripe-data-content">
          {/* Header */}
          <div className="stripe-header-section">
            <div className="stripe-header-main">
              <div className="stripe-title-section">
                <h1 className="stripe-page-title">
                  <CreditCard className="stripe-title-icon" />
                  Square Customer Analytics
                </h1>
                <p className="stripe-page-subtitle">
                  Track and analyze customer payment data from Square
                </p>
              </div>
              <div className="stripe-search-section">
                <div className="stripe-search-wrapper">
                  <Search className="stripe-search-icon" size={18} />
                  <input
                    type="email"
                    className="stripe-search-input"
                    placeholder="Search by customer email..."
                    value={searchMail}
                    onChange={(e) => setSearchMail(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button className="stripe-search-button" onClick={handleSearch}>
                    <Search size={16} />
                    Search
                  </button>
                </div>
              </div>
            </div>

            {/* Customer Info Card */}
            {customerData && (
              <div className="stripe-customer-card">
                <div className="stripe-customer-header">
                  <div className="stripe-customer-avatar">
                    <User size={24} />
                  </div>
                  <div className="stripe-customer-info">
                    <h3 className="stripe-customer-name">
                      {customerData?.emailAddress || customerData?.givenName || "-"}
                    </h3>
                    <div className="stripe-customer-meta">
                      <span className="stripe-customer-id">
                        <Shield size={14} />
                        {customerData?.id}
                      </span>
                      <span className="stripe-customer-id">
                        <Clock size={14} />
                        Created: {formatDate(customerData?.createdAt)}
                      </span>
                    </div>
                  </div>
                  <button
                    className="stripe-copy-button"
                    onClick={() => handleCopy(customerData?.id)}
                    title="Copy Customer ID"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Data Sections */}
          <div className="stripe-data-sections">
            {/* Square Data */}
            <div className="stripe-data-section">
              <div className="stripe-section-header">
                <CreditCard className="stripe-section-icon" />
                <h2 className="stripe-section-title">Square Data</h2>
              </div>
              <div className="stripe-tables-grid">
                {/* Subscriptions */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <RefreshCw className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Subscriptions</h3>
                    <span className="stripe-table-count">{subscriptions.length}</span>
                  </div>
                  <SquareSubscriptionsTable data={subscriptions} onCopy={handleCopy} />
                </div>

                {/* Payments */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <DollarSign className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Payments</h3>
                    <span className="stripe-table-count">{payments.length}</span>
                  </div>
                  <SquarePaymentsTable data={payments} />
                </div>
              </div>
            </div>

            {/* Database Records */}
            <div className="stripe-data-section">
              <div className="stripe-section-header">
                <Database className="stripe-section-icon" />
                <h2 className="stripe-section-title">Database Records</h2>
              </div>
              <div className="stripe-tables-grid">
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <Clock className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Payment History</h3>
                    <span className="stripe-table-count">{dbLogs.length}</span>
                  </div>
                  <DbLogsTable data={dbLogs} />
                </div>
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <User className="stripe-table-icon" />
                    <h3 className="stripe-table-title">User Details</h3>
                    <span className="stripe-table-count">{dbUser.length}</span>
                  </div>
                  <UserDetailsTable data={dbUser} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loader && <Loader />}
    </>
  );
}

const SquareSubscriptionsTable = ({ data, onCopy }) => {
  const fmt = (d) => (d ? moment(d).format("MMM Do YYYY") : "-");
  return (
    <div className="stripe-table-wrapper">
      <table className="stripe-data-table">
        <thead>
          <tr>
            <th>Subscription ID</th>
            <th>Status</th>
            <th>Start Date</th>
            <th>Charged Through</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr key={i} className="stripe-data-row">
              <td>
                <div className="stripe-cell-content">
                  <code className="stripe-id">{item?.id}</code>
                  <button
                    className="stripe-copy-small"
                    onClick={() => onCopy(item?.id)}
                    title="Copy ID"
                  >
                    <Copy size={10} />
                  </button>
                </div>
              </td>
              <td>
                {item?.displayStatus === "CANCELED_PENDING" ? (
                  <span className="stripe-badge stripe-badge-error">
                    <XCircle size={12} /> Cancels {fmt(item?.canceledDate || item?.canceled_date)}
                  </span>
                ) : item?.displayStatus === "ACTIVE" || item?.status === "ACTIVE" ? (
                  <span className="stripe-badge stripe-badge-success">
                    <CheckCircle size={12} /> Active
                  </span>
                ) : (
                  <span className="stripe-badge stripe-badge-error">
                    <XCircle size={12} /> {item?.displayStatus || item?.status}
                  </span>
                )}
              </td>
              <td>
                <div className="stripe-date-cell">
                  <Calendar size={12} /> {fmt(item?.startDate)}
                </div>
              </td>
              <td>
                <div className="stripe-date-cell">
                  <Calendar size={12} /> {fmt(item?.chargedThroughDate)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="stripe-empty-state">
          <RefreshCw size={32} />
          <h4>No Subscriptions Found</h4>
          <p>Search for a customer to view subscription data</p>
        </div>
      )}
    </div>
  );
};

const SquarePaymentsTable = ({ data }) => {
  const fmt = (d) => (d ? moment(d).format("MMM Do YYYY, h:mm a") : "-");
  return (
    <div className="stripe-table-wrapper">
      <table className="stripe-data-table">
        <thead>
          <tr>
            <th>Payment ID</th>
            <th>Email</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date</th>
            <th>Card</th>
            <th>Receipt</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr key={i} className="stripe-data-row">
              <td>
                <code className="stripe-id">{item?.id?.substring(0, 14)}...</code>
              </td>
              <td>{item?.buyerEmailAddress || "-"}</td>
              <td>
                <div className="stripe-amount-cell">
                  <DollarSign size={12} />
                  {((item?.amountMoney?.amount || 0) / 100).toFixed(2)}
                  <span className="stripe-currency">{item?.amountMoney?.currency || "USD"}</span>
                </div>
              </td>
              <td>
                {item?.status === "COMPLETED" ? (
                  <span className="stripe-badge stripe-badge-success">
                    <CheckCircle size={12} /> Completed
                  </span>
                ) : (
                  <span className="stripe-badge stripe-badge-error">
                    <XCircle size={12} /> {item?.status}
                  </span>
                )}
              </td>
              <td>
                <div className="stripe-date-cell">
                  <Calendar size={12} /> {fmt(item?.createdAt)}
                </div>
              </td>
              <td>
                {item?.cardDetails?.card?.last4 ? (
                  <span className="stripe-card-number">•••• {item.cardDetails.card.last4}</span>
                ) : (
                  <span className="stripe-text-muted">-</span>
                )}
              </td>
              <td>
                {item?.receiptUrl ? (
                  <a
                    href={item.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="stripe-invoice-link"
                  >
                    <ExternalLink size={12} /> View
                  </a>
                ) : (
                  <span className="stripe-text-muted">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="stripe-empty-state">
          <DollarSign size={32} />
          <h4>No Payments Found</h4>
          <p>Search for a customer to view payment data</p>
        </div>
      )}
    </div>
  );
};

const DbLogsTable = ({ data }) => {
  const fmt = (d) => (d ? moment(d).format("MMM Do YYYY, h:mm a") : "-");
  return (
    <div className="stripe-table-wrapper">
      <table className="stripe-data-table">
        <thead>
          <tr>
            <th>Amount</th>
            <th>Type</th>
            <th>Paid At</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr key={i} className="stripe-data-row">
              <td>
                <div className="stripe-amount-cell">
                  <DollarSign size={12} /> {item?.amountPaid || "0"}
                </div>
              </td>
              <td>
                <span className="stripe-text-muted">{item?.type === "1" ? "Monthly" : item?.type}</span>
              </td>
              <td>
                <div className="stripe-date-cell">
                  <Calendar size={12} /> {fmt(item?.createdAt)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="stripe-empty-state">
          <Clock size={32} />
          <h4>No Payment History</h4>
          <p>Search for a customer to view payment history</p>
        </div>
      )}
    </div>
  );
};

const UserDetailsTable = ({ data }) => {
  const fmt = (d) => (d ? moment(d).format("MMM Do YYYY") : "-");
  const isActive = (sub) => {
    if (!sub?.nextPayment) return false;
    return new Date() < new Date(sub.nextPayment);
  };
  return (
    <div className="stripe-table-wrapper">
      <table className="stripe-data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Contact</th>
            <th>Location</th>
            <th>Verified</th>
            <th>Subscription</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr key={i} className="stripe-data-row">
              <td>
                <div className="stripe-user-cell">
                  <div className="stripe-user-name">{item?.name || "-"}</div>
                  <div className="stripe-user-joined">
                    <Calendar size={10} /> Joined {fmt(item?.createdAt)}
                  </div>
                </div>
              </td>
              <td>
                <div className="stripe-contact-cell">
                  <div className="stripe-contact-item">
                    <Mail size={12} /> {item?.email || "-"}
                  </div>
                  {item?.phone && (
                    <div className="stripe-contact-item">
                      <Phone size={12} /> {item.phone}
                    </div>
                  )}
                </div>
              </td>
              <td>
                {item?.country ? (
                  <div className="stripe-location-cell">
                    <MapPin size={12} /> {item.country}
                  </div>
                ) : (
                  <span className="stripe-text-muted">-</span>
                )}
              </td>
              <td>
                {item?.emailVerified ? (
                  <span className="stripe-badge stripe-badge-success">
                    <CheckCircle size={12} /> Verified
                  </span>
                ) : (
                  <span className="stripe-badge stripe-badge-error">
                    <XCircle size={12} /> Not Verified
                  </span>
                )}
              </td>
              <td>
                {item?.subscription?.length > 0 ? (
                  isActive(item.subscription[0]) ? (
                    <span className="stripe-badge stripe-badge-success">Subscribed</span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">Expired</span>
                  )
                ) : (
                  <span className="stripe-text-muted">No Subscription</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="stripe-empty-state">
          <User size={32} />
          <h4>No User Data Found</h4>
          <p>Search for a customer to view user details</p>
        </div>
      )}
    </div>
  );
};

export default CheckSquareCustomerData;
